import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  TextField,
  Checkbox,
  Modal,
  Button,
} from '@mui/material';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

function ProjectDisplay({ boxes, onBoxesChange, handleRemove, handleEdit }) {
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({
    name: '',
    customerName: '',
    startMonth: '',
    startYear: '',
    endMonth: '',
    endYear: '',
    onGoing: false,
  });
  const [open, setOpen] = useState(false);

  // Handle drag end to reorder boxes
  const onDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedBoxes = Array.from(boxes);
    const [movedBox] = reorderedBoxes.splice(result.source.index, 1);
    reorderedBoxes.splice(result.destination.index, 0, movedBox);

    onBoxesChange(reorderedBoxes);
  };

  // Handle edit button click (open modal)
  /* const handleEdit = (box) => {
    setEditingId(box._id);
    setEditValues({
      name: box.name || '',
      customerName: box.customerName || '',
      startMonth: box.startMonth || '',
      startYear: box.startYear || '',
      endMonth: box.endMonth || '',
      endYear: box.endYear || '',
      onGoing: box.onGoing || false,
    });
    setOpen(true);
  }; */

  // Handle modal close
  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
  };

  // Handle save changes
  const handleSave = () => {
    const updatedBoxes = boxes.map((b) =>
      b._id === editingId ? { ...b, ...editValues } : b
    );
    onBoxesChange(updatedBoxes);
    handleClose();
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditValues((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle switch changes
  const toggleField = (_id, field) => {
    const updatedBoxes = boxes.map((box) =>
      box._id === _id ? { ...box, [field]: !box[field] } : box
    );
    onBoxesChange(updatedBoxes);
  };

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="boxes">
          {(provided, snapshot) => (
            <Box
              {...provided.droppableProps}
              ref={provided.innerRef}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                padding: '16px',
              }}
            >
              {boxes?.map((box, index) => (
                <Draggable key={box._id} draggableId={box._id} index={index}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      sx={{
                        borderRadius: '50px',
                        padding: '16px',
                        backgroundColor: '#DED8D1',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        boxShadow: snapshot.isDragging
                          ? '0 4px 8px rgba(0,0,0,0.2)'
                          : 'none',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        {/* Left side: project info */}
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: '18px',
                              fontFamily: 'Readex Pro',
                              fontWeight: 500,
                            }}
                          >
                            {box.name || 'Untitled Project'}
                          </Typography>
                          <Typography
                            sx={{
                              color: '#463C3B',
                              fontFamily: 'Readex Pro',
                              fontSize: '14px',
                              fontWeight: 400,
                            }}
                          >
                            {box.customerName || ''},{' '}
                            <span>
                              {box.startMonth || ''}/{box.startYear || ''} –{' '}
                              {box.onGoing
                                ? 'Present'
                                : `${box.endMonth || ''}/${box.endYear || ''}`}
                            </span>
                          </Typography>
                        </Box>

                        {/* Right side: actions + switches */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <IconButton
                            aria-label="edit"
                            size="small"
                            onClick={() => handleEdit(box._id, "editnew")}
                          >
                            <ModeEditOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            aria-label="delete"
                            size="small"
                            onClick={() => handleRemove(box._id)}
                          >
                            <HighlightOffOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={box.highlightInCv || false}
                                  onChange={() => toggleField(box._id, 'highlightInCv')}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: '#463C3B',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: '#463C3B',
                                    },
                                  }}
                                />
                              }
                              label="Highlight in CV"
                              sx={{
                                margin: 0,
                                '& .MuiTypography-root': {
                                  fontSize: '12px',
                                  fontFamily: 'Readex Pro',
                                  fontWeight: '300',
                                },
                              }}
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={!box.excludeInCv}
                                  onChange={() => toggleField(box._id, 'excludeInCv')}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: '#463C3B',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: '#463C3B',
                                    },
                                  }}
                                />
                              }
                              label="Include in CV"
                              sx={{
                                margin: 0,
                                '& .MuiTypography-root': {
                                  fontSize: '12px',
                                  fontFamily: 'Readex Pro',
                                  fontWeight: '300',
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      {/* Edit Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: '8px',
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Edit Project
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <TextField
              name="name"
              label="Project Name"
              value={editValues.name}
              onChange={handleInputChange}
              size="small"
              fullWidth
            />
            <TextField
              name="customerName"
              label="Customer Name"
              value={editValues.customerName}
              onChange={handleInputChange}
              size="small"
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: '8px' }}>
              <TextField
                name="startMonth"
                label="Start Month"
                value={editValues.startMonth}
                onChange={handleInputChange}
                size="small"
                sx={{ width: '100px' }}
              />
              <TextField
                name="startYear"
                label="Start Year"
                value={editValues.startYear}
                onChange={handleInputChange}
                size="small"
                sx={{ width: '100px' }}
              />
            </Box>
            {!editValues.onGoing && (
              <Box sx={{ display: 'flex', gap: '8px' }}>
                <TextField
                  name="endMonth"
                  label="End Month"
                  value={editValues.endMonth}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ width: '100px' }}
                />
                <TextField
                  name="endYear"
                  label="End Year"
                  value={editValues.endYear}
                  onChange={handleInputChange}
                  size="small"
                  sx={{ width: '100px' }}
                />
              </Box>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  name="onGoing"
                  checked={editValues.onGoing}
                  onChange={handleInputChange}
                  size="small"
                />
              }
              label="Ongoing"
              sx={{ margin: 0 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', mt: 2 }}>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSave}>
                Save
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default ProjectDisplay;